import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout'
import { Observable, Subject, timer } from 'rxjs'
import { filter, first, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators'
import { NavigationEnd, Router } from '@angular/router'
import { MatMenuTrigger } from '@angular/material/menu'
import { AppMessages, AppMessageValue, AppState, CorpMemberState } from '../ngrx-store/app.state'
import { select, Store } from '@ngrx/store'
import { CallerService } from '../services/caller.service'
import { FormControl } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { SearchDialogComponent } from '../dialogs/search-dialog/search-dialog.component'
import { environment } from '../../environments/environment'
import { PushNotificationService } from '../services/push-notification.service'

const OFFER_PUSH_ON_DEVICE_DELAY_MS = 4000

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.css'],
})
export class NavComponent implements OnInit, OnDestroy {
  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger | undefined

  unreadMessage: number = 0

  // TODO: should be called from the base or caller service
  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map((result) => result.matches),
      shareReplay()
    )

  menuState = ''

  searchInput: FormControl = new FormControl('')

  // This nav is rendered directly inside the /search route's own template (see
  // search-page.component.html), so on that route the search bar below would duplicate
  // the search form SearchResultsComponent already renders on the page itself.
  isSearchPage = false

  /** The onboarding checklist doubles as the wishlist; see OnboardingChecklistComponent. */
  readonly wishlistEnabled = environment.ONBOARDING_CHECKLIST_ENABLED

  private destroy$ = new Subject<void>()

  /**
   * copied from https://github.com/angular/components/issues/15578#issuecomment-475792789
   * with a bit of refactor.
   */
  toggleMenuState(): void {
    this.menuState = this.menuState == '' ? '_open' : ''
  }

  constructor(
    private breakpointObserver: BreakpointObserver,
    public store: Store<AppState>,
    private router: Router,

    public callerService: CallerService,
    private dialog: MatDialog,
    private pushNotificationService: PushNotificationService,

    private cdf: ChangeDetectorRef
  ) {
    this.isSearchPage = this.router.url.startsWith('/search')
  }

  logout(): void {
    this.callerService.logout();
  }

  ngOnInit() {
    // `?wishlist=open` (the wishlist alert email's "Manage your wishlist" link) opens it on any page.
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe((event) => this.openWishlistFromUrl(event.urlAfterRedirects))
    this.openWishlistFromUrl(this.router.url) // in case this nav was created after the navigation ended

    // Wishlist alerts on this device too, if it doesn't get notifications yet (once per account per
    // device). After a short wait, so it doesn't land on top of the page loading.
    if (this.wishlistEnabled) {
      timer(OFFER_PUSH_ON_DEVICE_DELAY_MS)
        .pipe(
          switchMap(() => this.store.pipe(select('corper'))),
          first((corper: CorpMemberState) => !!corper?.id),
          takeUntil(this.destroy$),
        )
        .subscribe((corper: CorpMemberState) => this.pushNotificationService.maybeOfferOnThisDevice(corper))
    }

    // Chat messages / broadcasts (new sale/accommodation posts) update this store slice
    // regardless of which page is mounted — see SocketEffects, which owns those socket
    // listeners for the whole app session instead of any single component.
    this.store.pipe(select('messages')).subscribe({
      next: (value: AppMessages) => {
        if (value.size < 1) {
          return
        }
        this.unreadMessage = Array.from(value.values()).reduce(
          (acc: number, cv: AppMessageValue) => {
            return acc + Number(cv?.unread_messages || 0)
          },
          0
        )

        this.cdf.detectChanges()
      },
    })
  }

  openWishlist() {
    this.callerService.openOnboardingChecklist().subscribe()
  }

  private openWishlistFromUrl(url: string): void {
    const tree = this.router.parseUrl(url)
    if (tree.queryParams['wishlist'] !== 'open' || !this.wishlistEnabled || !this.callerService.isLoggedIn) {
      return
    }
    // Drop the param first, so a reload or Back doesn't open it again.
    this.router.navigate([], { queryParams: { wishlist: null }, queryParamsHandling: 'merge', replaceUrl: true })
    this.openWishlist()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  openSearchModal() {
    this.dialog.open(SearchDialogComponent, {
      width: '100%',
      height: '100%',
      maxWidth: '100vw',
      // data: { searchText: 'some value here?' }
    });
  }
}
